import React, { useState, useEffect } from 'react';
import { Plus, TrendingUp, Users, MapPin, DollarSign, Eye, Mail, Shield, Loader2, Building, Globe, Briefcase, Clock, FileText, Lock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { maApi, profileApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTier } from '../context/TierContext';
import RestrictedOverlay from './ui/RestrictedOverlay';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';

const MergersAcquisitions = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showListingDialog, setShowListingDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showEnquiryDialog, setShowEnquiryDialog] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [enquiryMessage, setEnquiryMessage] = useState('');
  const [memberProfile, setMemberProfile] = useState(null);
  const [enquiryTab, setEnquiryTab] = useState('investment');

  // Enhanced Enquiry Form State
  const [enquiryForm, setEnquiryForm] = useState({
    // Investment Intent
    interest_type: 'Full Acquisition',
    stake_percentage: '',
    investment_budget: '',
    funding_source: 'Self-funded',
    target_timeline: 'Short-term (3-6 months)',

    // Strategic Rationale
    primary_reason: 'Geographic Expansion',
    expected_synergies: '',
    target_regions: [],

    // Buyer Qualifications
    prior_ma_experience: false,
    deals_completed: '',
    current_annual_revenue: '',
    years_in_logistics: '',
    key_services: [],

    // Due Diligence & Process
    key_questions: '',
    nda_willingness: 'Ready to sign immediately',
    preferred_meeting_format: 'Video Call',

    // Confidentiality
    keep_confidential: true,

    // Additional message
    message: ''
  });

  const resetEnquiryForm = () => {
    setEnquiryForm({
      interest_type: 'Full Acquisition',
      stake_percentage: '',
      investment_budget: '',
      funding_source: 'Self-funded',
      target_timeline: 'Short-term (3-6 months)',
      primary_reason: 'Geographic Expansion',
      expected_synergies: '',
      target_regions: [],
      prior_ma_experience: false,
      deals_completed: '',
      current_annual_revenue: '',
      years_in_logistics: '',
      key_services: [],
      key_questions: '',
      nda_willingness: 'Ready to sign immediately',
      preferred_meeting_format: 'Video Call',
      keep_confidential: true,
      message: ''
    });
    setEnquiryTab('investment');
  };

  const { member } = useAuth();
  const { hasRestriction, getRestrictionMessage, hasFeatureAccess } = useTier();
  const currentMemberId = member?.member_id;

  // Preview mode - allow all tiers to view M&A section for demo purposes (disabled by default)
  const isPreviewMode = process.env.REACT_APP_MA_PREVIEW_MODE === 'true';

  const [listingForm, setListingForm] = useState({
    dealType: 'Full Stake Sale',
    yearsOfExistence: '',
    teamSize: '',
    revenue: { year1: '', year2: '', year3: '' },
    grossProfit: { year1: '', year2: '', year3: '' },
    netProfit: { year1: '', year2: '', year3: '' },
    monthlyVolume: {
      ocean: '',
      air: '',
      lcl: '',
      projects: ''
    },
    keyStrengths: '',
    locations: '',
    specialization: '',
    expectedValuation: '',
    reasonForSale: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [profileRes] = await Promise.all([
        profileApi.getProfile(currentMemberId)
      ]);
      setMemberProfile(profileRes.data);

      // Try to fetch listings - may fail due to tier restrictions
      try {
        const listingsRes = await maApi.getListings(currentMemberId);
        setListings(listingsRes.data);
      } catch (error) {
        console.error('Failed to fetch M&A listings:', error);
        setListings([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const isCompanyAdmin =
    memberProfile?.role === 'Company Admin' ||
    memberProfile?.has_admin_rights === true ||
    memberProfile?.is_company_admin === true;

  const canAccessMaPortal =
    isPreviewMode ||
    isCompanyAdmin ||
    hasFeatureAccess?.('ma_portal');

  const isOwnerOrKMP =
    isPreviewMode ||
    isCompanyAdmin ||
    memberProfile?.role === 'Owner / Founder' ||
    memberProfile?.role === 'Key Management Personnel';

  const officeLocationsHint = (() => {
    const parts = [memberProfile?.city, memberProfile?.country].filter(Boolean);
    const unique = parts.filter((v, i) => parts.indexOf(v) === i);
    return unique.join(', ') || 'e.g., Singapore';
  })();

  // Auto-fill ONLY locations from the member's office.
  // Keep other listing fields empty as requested.
  useEffect(() => {
    if (!memberProfile) return;
    setListingForm((prev) => {
      // Only set once if the user hasn't typed locations yet.
      if (prev.locations && prev.locations.trim()) return prev;
      const parts = [memberProfile?.city, memberProfile?.country].filter(Boolean);
      const unique = parts.filter((v, i) => parts.indexOf(v) === i);
      if (unique.length === 0) return prev;
      return { ...prev, locations: unique.join(', ') };
    });
  }, [memberProfile]);

  // Intentionally do NOT auto-fill listing fields (except Locations).

  const handleSubmitListing = async (e) => {
    e.preventDefault();
    try {
      const listingData = {
        company_id: memberProfile?.company_id,
        company_name: memberProfile?.company_name,
        country: memberProfile?.country,
        deal_type: listingForm.dealType,
        years_of_existence: parseInt(listingForm.yearsOfExistence),
        team_size: parseInt(listingForm.teamSize),
        revenue: {
          year1: parseFloat(listingForm.revenue.year1),
          year2: parseFloat(listingForm.revenue.year2),
          year3: parseFloat(listingForm.revenue.year3)
        },
        gross_profit: {
          year1: parseFloat(listingForm.grossProfit.year1),
          year2: parseFloat(listingForm.grossProfit.year2),
          year3: parseFloat(listingForm.grossProfit.year3)
        },
        net_profit: {
          year1: parseFloat(listingForm.netProfit.year1),
          year2: parseFloat(listingForm.netProfit.year2),
          year3: parseFloat(listingForm.netProfit.year3)
        },
        monthly_volume: {
          ocean_teu: listingForm.monthlyVolume.ocean,
          air_tons: listingForm.monthlyVolume.air,
          lcl_cbm: listingForm.monthlyVolume.lcl,
          projects_per_year: listingForm.monthlyVolume.projects
        },
        key_strengths: listingForm.keyStrengths.split(',').map(s => s.trim()),
        locations: listingForm.locations.split(',').map(s => s.trim()),
        specialization: listingForm.specialization.split(',').map(s => s.trim()),
        expected_valuation: parseFloat(listingForm.expectedValuation),
        reason_for_sale: listingForm.reasonForSale
      };

      await maApi.createListing(listingData, currentMemberId);
      toast.success('M&A listing submitted successfully');
      setShowListingDialog(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to submit listing'));
    }
  };

  const handleEnquire = async () => {
    if (!selectedListing) {
      toast.error('No listing selected');
      return;
    }

    // Validate required fields
    if (!enquiryForm.interest_type) {
      toast.error('Please select your interest type');
      return;
    }
    if (enquiryForm.interest_type === 'Partial Stake' && !enquiryForm.stake_percentage) {
      toast.error('Please specify the stake percentage');
      return;
    }
    if (!enquiryForm.investment_budget) {
      toast.error('Please select your investment budget range');
      return;
    }

    try {
      const enquiryData = {
        listing_id: selectedListing.listing_id,
        enquirer_id: currentMemberId,
        enquirer_company_id: memberProfile?.company_id,
        enquirer_company_name: memberProfile?.company_name,

        // Investment Intent
        interest_type: enquiryForm.interest_type,
        stake_percentage: enquiryForm.stake_percentage ? parseInt(enquiryForm.stake_percentage) : null,
        investment_budget: enquiryForm.investment_budget,
        funding_source: enquiryForm.funding_source,
        target_timeline: enquiryForm.target_timeline,

        // Strategic Rationale
        primary_reason: enquiryForm.primary_reason,
        expected_synergies: enquiryForm.expected_synergies,
        target_regions: enquiryForm.target_regions,

        // Buyer Qualifications
        prior_ma_experience: enquiryForm.prior_ma_experience,
        deals_completed: enquiryForm.deals_completed ? parseInt(enquiryForm.deals_completed) : 0,
        current_annual_revenue: enquiryForm.current_annual_revenue,
        years_in_logistics: enquiryForm.years_in_logistics ? parseInt(enquiryForm.years_in_logistics) : null,
        key_services: enquiryForm.key_services,

        // Due Diligence & Process
        key_questions: enquiryForm.key_questions,
        nda_willingness: enquiryForm.nda_willingness,
        preferred_meeting_format: enquiryForm.preferred_meeting_format,

        // Confidentiality
        keep_confidential: enquiryForm.keep_confidential,

        // Additional message
        message: enquiryForm.message,
        status: 'Pending'
      };

      await maApi.expressInterest(selectedListing.listing_id, enquiryData, currentMemberId);
      toast.success('Your interest has been expressed successfully');
      setShowEnquiryDialog(false);
      resetEnquiryForm();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to send enquiry'));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (hasRestriction('ma')) {
    return <RestrictedOverlay message={getRestrictionMessage('ma')} />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading M&A listings...</span>
      </div>
    );
  }

  if (!canAccessMaPortal && !isPreviewMode) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Mergers & Acquisitions</h1>
          <p className="text-gray-600 mt-1">Strategic opportunities for business growth</p>
        </div>

        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-8 text-center">
            <Shield size={48} className="mx-auto text-amber-500 mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Restricted Access</h2>
            <p className="text-gray-600 mb-4">
              The M&A Portal is restricted for your current tier. Upgrade to access listing and enquiries.
              Upgrade your membership to access strategic acquisition opportunities.
            </p>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white">
              Upgrade to Titanium
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ma-section">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Mergers & Acquisitions</h1>
          <p className="text-gray-600 mt-1">Strategic opportunities for business growth</p>
          {isPreviewMode && (
            <Badge className="mt-2 bg-amber-100 text-amber-700">Preview Mode - All tiers can view</Badge>
          )}
        </div>
        {isOwnerOrKMP && (
          <Dialog open={showListingDialog} onOpenChange={setShowListingDialog}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="create-listing-btn">
                <Plus size={16} className="mr-2" />
                List Your Business
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>List Your Business for M&A</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmitListing} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Deal Type</Label>
                    <select
                      className="w-full px-3 py-2 border rounded-md"
                      value={listingForm.dealType}
                      onChange={(e) => setListingForm({ ...listingForm, dealType: e.target.value })}
                    >
                      <option value="Full Stake Sale">Full Stake Sale</option>
                      <option value="Partial Stake Sale">Partial Stake Sale</option>
                      <option value="Joint Venture">Joint Venture</option>
                      <option value="Merger">Merger</option>
                    </select>
                  </div>
                  <div>
                    <Label>Years in Business</Label>
                    <Input
                      type="number"
                      value={listingForm.yearsOfExistence}
                      onChange={(e) => setListingForm({ ...listingForm, yearsOfExistence: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label>Team Size</Label>
                  <Input
                    type="number"
                    value={listingForm.teamSize}
                    onChange={(e) => setListingForm({ ...listingForm, teamSize: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Revenue (Last 3 Years, USD)</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {['year1', 'year2', 'year3'].map((year, i) => (
                      <Input
                        key={year}
                        type="number"
                        placeholder={`Year ${i + 1} (USD)`}
                        value={listingForm.revenue[year]}
                        onChange={(e) => setListingForm({
                          ...listingForm,
                          revenue: { ...listingForm.revenue, [year]: e.target.value }
                        })}
                        required
                      />
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Gross Profit (Last 3 Years, USD)</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {['year1', 'year2', 'year3'].map((year, i) => (
                      <Input
                        key={year}
                        type="number"
                        placeholder={`Year ${i + 1} (USD)`}
                        value={listingForm.grossProfit[year]}
                        onChange={(e) => setListingForm({
                          ...listingForm,
                          grossProfit: { ...listingForm.grossProfit, [year]: e.target.value }
                        })}
                        required
                      />
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Net Profit (Last 3 Years, USD)</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {['year1', 'year2', 'year3'].map((year, i) => (
                      <Input
                        key={year}
                        type="number"
                        placeholder={`Year ${i + 1} (USD)`}
                        value={listingForm.netProfit[year]}
                        onChange={(e) => setListingForm({
                          ...listingForm,
                          netProfit: { ...listingForm.netProfit, [year]: e.target.value }
                        })}
                        required
                      />
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Monthly Volume</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Ocean (e.g., 150 TEU)"
                      value={listingForm.monthlyVolume.ocean}
                      onChange={(e) => setListingForm({
                        ...listingForm,
                        monthlyVolume: { ...listingForm.monthlyVolume, ocean: e.target.value }
                      })}
                    />
                    <Input
                      placeholder="Air (e.g., 80 tons)"
                      value={listingForm.monthlyVolume.air}
                      onChange={(e) => setListingForm({
                        ...listingForm,
                        monthlyVolume: { ...listingForm.monthlyVolume, air: e.target.value }
                      })}
                    />
                    <Input
                      placeholder="LCL (e.g., 200 CBM)"
                      value={listingForm.monthlyVolume.lcl}
                      onChange={(e) => setListingForm({
                        ...listingForm,
                        monthlyVolume: { ...listingForm.monthlyVolume, lcl: e.target.value }
                      })}
                    />
                    <Input
                      placeholder="Projects/Year (e.g., 5-6)"
                      value={listingForm.monthlyVolume.projects}
                      onChange={(e) => setListingForm({
                        ...listingForm,
                        monthlyVolume: { ...listingForm.monthlyVolume, projects: e.target.value }
                      })}
                    />
                  </div>
                </div>

                <div>
                  <Label>Key Strengths (comma separated)</Label>
                  <Textarea
                    value={listingForm.keyStrengths}
                    onChange={(e) => setListingForm({ ...listingForm, keyStrengths: e.target.value })}
                    placeholder="Strong network, Established customer base, Modern IT systems"
                    required
                  />
                </div>

                <div>
                  <Label>Locations (comma separated)</Label>
                  <Input
                    value={listingForm.locations}
                    onChange={(e) => setListingForm({ ...listingForm, locations: e.target.value })}
                    placeholder={officeLocationsHint}
                    required
                  />
                </div>

                <div>
                  <Label>Specializations (comma separated)</Label>
                  <Input
                    value={listingForm.specialization}
                    onChange={(e) => setListingForm({ ...listingForm, specialization: e.target.value })}
                    placeholder="Air Freight, E-commerce, Dangerous Goods"
                    required
                  />
                </div>

                <div>
                  <Label>Expected Valuation (USD)</Label>
                  <Input
                    type="number"
                    value={listingForm.expectedValuation}
                    onChange={(e) => setListingForm({ ...listingForm, expectedValuation: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <Label>Reason for Sale</Label>
                  <Textarea
                    value={listingForm.reasonForSale}
                    onChange={(e) => setListingForm({ ...listingForm, reasonForSale: e.target.value })}
                    placeholder="Describe your motivation for this strategic move..."
                    rows={3}
                    required
                  />
                </div>

                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => setShowListingDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white">
                    Submit Listing
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <Building size={24} className="mx-auto text-blue-600 mb-2" />
            <p className="text-2xl font-bold">{listings.length}</p>
            <p className="text-sm text-gray-600">Active Listings</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <TrendingUp size={24} className="mx-auto text-green-600 mb-2" />
            <p className="text-2xl font-bold">
              {formatCurrency(listings.reduce((sum, l) => sum + (l.expected_valuation || 0), 0))}
            </p>
            <p className="text-sm text-gray-600">Total Value</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Eye size={24} className="mx-auto text-purple-600 mb-2" />
            <p className="text-2xl font-bold">
              {listings.reduce((sum, l) => sum + (l.view_count || 0), 0)}
            </p>
            <p className="text-sm text-gray-600">Total Views</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Mail size={24} className="mx-auto text-amber-600 mb-2" />
            <p className="text-2xl font-bold">
              {listings.reduce((sum, l) => sum + (l.enquiries_count || 0), 0)}
            </p>
            <p className="text-sm text-gray-600">Enquiries</p>
          </CardContent>
        </Card>
      </div>

      {/* Listings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {listings.length === 0 ? (
          <Card className="col-span-2">
            <CardContent className="p-8 text-center">
              <Building size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No M&A listings available at the moment</p>
            </CardContent>
          </Card>
        ) : (
          listings.map((listing) => (
            <Card key={listing.listing_id} className="hover:shadow-lg transition-shadow" data-testid={`listing-${listing.listing_id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{listing.company_name}</h3>
                    <div className="flex items-center text-sm text-gray-600 mt-1">
                      <MapPin size={14} className="mr-1" />
                      {listing.country}
                    </div>
                  </div>
                  <Badge className={
                    listing.deal_type === 'Full Stake Sale' ? 'bg-red-100 text-red-700' :
                      listing.deal_type === 'Partial Stake Sale' ? 'bg-blue-100 text-blue-700' :
                        listing.deal_type === 'Joint Venture' ? 'bg-green-100 text-green-700' :
                          'bg-purple-100 text-purple-700'
                  }>
                    {listing.deal_type}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                  <div>
                    <p className="text-gray-500">Years in Business</p>
                    <p className="font-semibold">{listing.years_of_existence} years</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Team Size</p>
                    <p className="font-semibold">{listing.team_size} employees</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Expected Valuation</p>
                    <p className="font-semibold text-green-600">{formatCurrency(listing.expected_valuation)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Latest Revenue</p>
                    <p className="font-semibold">{formatCurrency(listing.revenue?.year3 || 0)}</p>
                  </div>
                </div>

                <div className="mb-4">
                  <p className="text-gray-500 text-sm mb-1">Specializations</p>
                  <div className="flex flex-wrap gap-1">
                    {(listing.specialization || []).slice(0, 3).map((spec, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{spec}</Badge>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span className="flex items-center">
                      <Eye size={14} className="mr-1" />
                      {listing.view_count} views
                    </span>
                    <span className="flex items-center">
                      <Mail size={14} className="mr-1" />
                      {listing.enquiries_count} enquiries
                    </span>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedListing(listing);
                        setShowDetailDialog(true);
                      }}
                    >
                      View Details
                    </Button>
                    <Button
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => {
                        setSelectedListing(listing);
                        setShowEnquiryDialog(true);
                      }}
                    >
                      <Mail size={14} className="mr-1" />
                      Enquire
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedListing?.company_name}</DialogTitle>
          </DialogHeader>
          {selectedListing && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <MapPin size={16} className="text-gray-500" />
                  <span>{selectedListing.country}</span>
                </div>
                <Badge className={
                  selectedListing.deal_type === 'Full Stake Sale' ? 'bg-red-100 text-red-700' :
                    'bg-blue-100 text-blue-700'
                }>
                  {selectedListing.deal_type}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-500">Expected Valuation</p>
                    <p className="text-2xl font-bold text-green-600">
                      {formatCurrency(selectedListing.expected_valuation)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-500">Team Size</p>
                    <p className="text-2xl font-bold">{selectedListing.team_size}</p>
                  </CardContent>
                </Card>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Financial Performance</h4>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left">Metric</th>
                      <th className="px-3 py-2 text-right">Year 1</th>
                      <th className="px-3 py-2 text-right">Year 2</th>
                      <th className="px-3 py-2 text-right">Year 3</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="px-3 py-2">Revenue</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(selectedListing.revenue?.year1)}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(selectedListing.revenue?.year2)}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(selectedListing.revenue?.year3)}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-3 py-2">Gross Profit</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(selectedListing.gross_profit?.year1)}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(selectedListing.gross_profit?.year2)}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(selectedListing.gross_profit?.year3)}</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2">Net Profit</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(selectedListing.net_profit?.year1)}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(selectedListing.net_profit?.year2)}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(selectedListing.net_profit?.year3)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Monthly Volumes</h4>
                <div className="grid grid-cols-4 gap-2 text-sm">
                  <div className="p-2 bg-gray-50 rounded">
                    <p className="text-gray-500">Ocean</p>
                    <p className="font-medium">{selectedListing.monthly_volume?.ocean_teu}</p>
                  </div>
                  <div className="p-2 bg-gray-50 rounded">
                    <p className="text-gray-500">Air</p>
                    <p className="font-medium">{selectedListing.monthly_volume?.air_tons}</p>
                  </div>
                  <div className="p-2 bg-gray-50 rounded">
                    <p className="text-gray-500">LCL</p>
                    <p className="font-medium">{selectedListing.monthly_volume?.lcl_cbm}</p>
                  </div>
                  <div className="p-2 bg-gray-50 rounded">
                    <p className="text-gray-500">Projects/Year</p>
                    <p className="font-medium">{selectedListing.monthly_volume?.projects_per_year}</p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Key Strengths</h4>
                <div className="flex flex-wrap gap-2">
                  {(selectedListing.key_strengths || []).map((strength, i) => (
                    <Badge key={i} variant="outline">{strength}</Badge>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Locations</h4>
                <div className="flex flex-wrap gap-2">
                  {(selectedListing.locations || []).map((loc, i) => (
                    <Badge key={i} className="bg-gray-100 text-gray-700">{loc}</Badge>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Reason for Sale</h4>
                <p className="text-gray-600">{selectedListing.reason_for_sale}</p>
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowDetailDialog(false)}>Close</Button>
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => {
                    setShowDetailDialog(false);
                    setShowEnquiryDialog(true);
                  }}
                >
                  <Mail size={16} className="mr-2" />
                  Express Interest
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Enquiry Dialog - Enhanced */}
      <Dialog open={showEnquiryDialog} onOpenChange={(open) => {
        setShowEnquiryDialog(open);
        if (!open) resetEnquiryForm();
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Mail size={20} className="mr-2 text-blue-600" />
              Express Interest - {selectedListing?.company_name}
            </DialogTitle>
          </DialogHeader>

          <Tabs value={enquiryTab} onValueChange={setEnquiryTab} className="w-full">
            <TabsList className="grid grid-cols-5 w-full">
              <TabsTrigger value="investment" className="text-xs">Investment</TabsTrigger>
              <TabsTrigger value="strategic" className="text-xs">Strategic</TabsTrigger>
              <TabsTrigger value="qualifications" className="text-xs">Qualifications</TabsTrigger>
              <TabsTrigger value="process" className="text-xs">Process</TabsTrigger>
              <TabsTrigger value="message" className="text-xs">Message</TabsTrigger>
            </TabsList>

            {/* Tab 1: Investment Intent */}
            <TabsContent value="investment" className="space-y-4 mt-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-1 flex items-center">
                  <DollarSign size={16} className="mr-2" />
                  Investment Intent
                </h3>
                <p className="text-sm text-blue-700">Define your investment parameters and timeline</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Interest Type *</Label>
                  <select
                    className="w-full px-3 py-2 border rounded-md mt-1"
                    value={enquiryForm.interest_type}
                    onChange={(e) => setEnquiryForm({ ...enquiryForm, interest_type: e.target.value })}
                  >
                    <option value="Full Acquisition">Full Acquisition (100%)</option>
                    <option value="Partial Stake">Partial Stake (%)</option>
                    <option value="Strategic Partnership">Strategic Partnership</option>
                    <option value="Joint Venture">Joint Venture</option>
                  </select>
                </div>

                {enquiryForm.interest_type === 'Partial Stake' && (
                  <div>
                    <Label>Target Stake Percentage *</Label>
                    <Input
                      type="number"
                      min="1"
                      max="99"
                      placeholder="e.g., 51"
                      value={enquiryForm.stake_percentage}
                      onChange={(e) => setEnquiryForm({ ...enquiryForm, stake_percentage: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                )}

                <div>
                  <Label>Investment Budget Range *</Label>
                  <select
                    className="w-full px-3 py-2 border rounded-md mt-1"
                    value={enquiryForm.investment_budget}
                    onChange={(e) => setEnquiryForm({ ...enquiryForm, investment_budget: e.target.value })}
                  >
                    <option value="">Select budget range</option>
                    <option value="$100K - $500K">$100K - $500K</option>
                    <option value="$500K - $1M">$500K - $1M</option>
                    <option value="$1M - $5M">$1M - $5M</option>
                    <option value="$5M - $10M">$5M - $10M</option>
                    <option value="$10M - $25M">$10M - $25M</option>
                    <option value="$25M+">$25M+</option>
                  </select>
                </div>

                <div>
                  <Label>Funding Source</Label>
                  <select
                    className="w-full px-3 py-2 border rounded-md mt-1"
                    value={enquiryForm.funding_source}
                    onChange={(e) => setEnquiryForm({ ...enquiryForm, funding_source: e.target.value })}
                  >
                    <option value="Self-funded">Self-funded</option>
                    <option value="Bank Loan">Bank Loan</option>
                    <option value="PE/VC Backed">PE/VC Backed</option>
                    <option value="Mixed">Mixed</option>
                  </select>
                </div>

                <div className="col-span-2">
                  <Label>Target Timeline</Label>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {['Immediate (1-3 months)', 'Short-term (3-6 months)', 'Medium-term (6-12 months)'].map((timeline) => (
                      <label
                        key={timeline}
                        className={`p-3 border rounded-lg cursor-pointer text-center text-sm transition-all ${enquiryForm.target_timeline === timeline
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-blue-300'
                          }`}
                      >
                        <input
                          type="radio"
                          name="timeline"
                          value={timeline}
                          checked={enquiryForm.target_timeline === timeline}
                          onChange={(e) => setEnquiryForm({ ...enquiryForm, target_timeline: e.target.value })}
                          className="sr-only"
                        />
                        {timeline}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Tab 2: Strategic Rationale */}
            <TabsContent value="strategic" className="space-y-4 mt-4">
              <div className="p-4 bg-green-50 rounded-lg">
                <h3 className="font-semibold text-green-900 mb-1 flex items-center">
                  <TrendingUp size={16} className="mr-2" />
                  Strategic Rationale
                </h3>
                <p className="text-sm text-green-700">Explain your strategic motivation and expected synergies</p>
              </div>

              <div>
                <Label>Primary Reason for Interest</Label>
                <select
                  className="w-full px-3 py-2 border rounded-md mt-1"
                  value={enquiryForm.primary_reason}
                  onChange={(e) => setEnquiryForm({ ...enquiryForm, primary_reason: e.target.value })}
                >
                  <option value="Geographic Expansion">Geographic Expansion</option>
                  <option value="Service Line Expansion">Service Line Expansion</option>
                  <option value="Market Entry">Market Entry</option>
                  <option value="Vertical Integration">Vertical Integration</option>
                  <option value="Portfolio Diversification">Portfolio Diversification</option>
                  <option value="Talent Acquisition">Talent Acquisition</option>
                  <option value="Technology/Systems Acquisition">Technology/Systems Acquisition</option>
                </select>
              </div>

              <div>
                <Label>Expected Synergies</Label>
                <Textarea
                  placeholder="Describe how this acquisition would benefit both parties... e.g., 'Combine our air freight expertise with their ocean network to offer end-to-end solutions'"
                  value={enquiryForm.expected_synergies}
                  onChange={(e) => setEnquiryForm({ ...enquiryForm, expected_synergies: e.target.value })}
                  rows={3}
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Target Regions of Interest</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {['Asia Pacific', 'Europe', 'North America', 'South America', 'Middle East', 'Africa'].map((region) => (
                    <label
                      key={region}
                      className={`p-2 border rounded-lg cursor-pointer text-center text-sm transition-all ${enquiryForm.target_regions.includes(region)
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-200 hover:border-green-300'
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={enquiryForm.target_regions.includes(region)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setEnquiryForm({ ...enquiryForm, target_regions: [...enquiryForm.target_regions, region] });
                          } else {
                            setEnquiryForm({ ...enquiryForm, target_regions: enquiryForm.target_regions.filter(r => r !== region) });
                          }
                        }}
                        className="sr-only"
                      />
                      <Globe size={14} className="mx-auto mb-1" />
                      {region}
                    </label>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Tab 3: Buyer Qualifications */}
            <TabsContent value="qualifications" className="space-y-4 mt-4">
              <div className="p-4 bg-purple-50 rounded-lg">
                <h3 className="font-semibold text-purple-900 mb-1 flex items-center">
                  <Briefcase size={16} className="mr-2" />
                  Buyer Qualifications
                </h3>
                <p className="text-sm text-purple-700">Share your experience and capabilities</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center">
                    Prior M&A Experience
                    <input
                      type="checkbox"
                      checked={enquiryForm.prior_ma_experience}
                      onChange={(e) => setEnquiryForm({ ...enquiryForm, prior_ma_experience: e.target.checked })}
                      className="ml-2"
                    />
                  </Label>
                  {enquiryForm.prior_ma_experience && (
                    <Input
                      type="number"
                      placeholder="Number of deals completed"
                      value={enquiryForm.deals_completed}
                      onChange={(e) => setEnquiryForm({ ...enquiryForm, deals_completed: e.target.value })}
                      className="mt-2"
                    />
                  )}
                </div>

                <div>
                  <Label>Years in Logistics Industry</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 15"
                    value={enquiryForm.years_in_logistics}
                    onChange={(e) => setEnquiryForm({ ...enquiryForm, years_in_logistics: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div className="col-span-2">
                  <Label>Current Annual Revenue</Label>
                  <select
                    className="w-full px-3 py-2 border rounded-md mt-1"
                    value={enquiryForm.current_annual_revenue}
                    onChange={(e) => setEnquiryForm({ ...enquiryForm, current_annual_revenue: e.target.value })}
                  >
                    <option value="">Select revenue range</option>
                    <option value="Under $1M">Under $1M</option>
                    <option value="$1M - $5M">$1M - $5M</option>
                    <option value="$5M - $10M">$5M - $10M</option>
                    <option value="$10M - $25M">$10M - $25M</option>
                    <option value="$25M - $50M">$25M - $50M</option>
                    <option value="$50M - $100M">$50M - $100M</option>
                    <option value="$100M+">$100M+</option>
                  </select>
                </div>

                <div className="col-span-2">
                  <Label>Key Services Offered</Label>
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {['Ocean Freight', 'Air Freight', 'Road Transport', 'Warehousing', 'Customs Brokerage', 'Project Cargo', 'E-commerce', 'Cross-Trade'].map((service) => (
                      <label
                        key={service}
                        className={`p-2 border rounded-lg cursor-pointer text-center text-xs transition-all ${enquiryForm.key_services.includes(service)
                            ? 'border-purple-500 bg-purple-50 text-purple-700'
                            : 'border-gray-200 hover:border-purple-300'
                          }`}
                      >
                        <input
                          type="checkbox"
                          checked={enquiryForm.key_services.includes(service)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setEnquiryForm({ ...enquiryForm, key_services: [...enquiryForm.key_services, service] });
                            } else {
                              setEnquiryForm({ ...enquiryForm, key_services: enquiryForm.key_services.filter(s => s !== service) });
                            }
                          }}
                          className="sr-only"
                        />
                        {service}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Tab 4: Due Diligence & Process */}
            <TabsContent value="process" className="space-y-4 mt-4">
              <div className="p-4 bg-amber-50 rounded-lg">
                <h3 className="font-semibold text-amber-900 mb-1 flex items-center">
                  <FileText size={16} className="mr-2" />
                  Due Diligence & Process
                </h3>
                <p className="text-sm text-amber-700">Your questions and preferred process</p>
              </div>

              <div>
                <Label>Key Questions/Concerns</Label>
                <Textarea
                  placeholder="List any initial questions you have for the seller..."
                  value={enquiryForm.key_questions}
                  onChange={(e) => setEnquiryForm({ ...enquiryForm, key_questions: e.target.value })}
                  rows={3}
                  className="mt-1"
                />
              </div>

              <div>
                <Label>NDA Willingness</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {['Ready to sign immediately', 'Need legal review', 'Prefer initial discussion first'].map((option) => (
                    <label
                      key={option}
                      className={`p-3 border rounded-lg cursor-pointer text-center text-sm transition-all ${enquiryForm.nda_willingness === option
                          ? 'border-amber-500 bg-amber-50 text-amber-700'
                          : 'border-gray-200 hover:border-amber-300'
                        }`}
                    >
                      <input
                        type="radio"
                        name="nda"
                        value={option}
                        checked={enquiryForm.nda_willingness === option}
                        onChange={(e) => setEnquiryForm({ ...enquiryForm, nda_willingness: e.target.value })}
                        className="sr-only"
                      />
                      {option}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label>Preferred Meeting Format</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {['Video Call', 'In-Person', 'Phone Call'].map((format) => (
                    <label
                      key={format}
                      className={`p-3 border rounded-lg cursor-pointer text-center text-sm transition-all ${enquiryForm.preferred_meeting_format === format
                          ? 'border-amber-500 bg-amber-50 text-amber-700'
                          : 'border-gray-200 hover:border-amber-300'
                        }`}
                    >
                      <input
                        type="radio"
                        name="meeting"
                        value={format}
                        checked={enquiryForm.preferred_meeting_format === format}
                        onChange={(e) => setEnquiryForm({ ...enquiryForm, preferred_meeting_format: e.target.value })}
                        className="sr-only"
                      />
                      {format}
                    </label>
                  ))}
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enquiryForm.keep_confidential}
                    onChange={(e) => setEnquiryForm({ ...enquiryForm, keep_confidential: e.target.checked })}
                    className="mr-3 w-5 h-5"
                  />
                  <div>
                    <p className="font-medium flex items-center">
                      <Lock size={14} className="mr-1" />
                      Keep my enquiry confidential
                    </p>
                    <p className="text-sm text-gray-500">Hide my identity from other potential buyers</p>
                  </div>
                </label>
              </div>
            </TabsContent>

            {/* Tab 5: Additional Message */}
            <TabsContent value="message" className="space-y-4 mt-4">
              <div className="p-4 bg-gray-100 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-1 flex items-center">
                  <Mail size={16} className="mr-2" />
                  Additional Message
                </h3>
                <p className="text-sm text-gray-600">Introduce yourself and add any other information</p>
              </div>

              <div>
                <Label>Personal Message to Seller (Optional)</Label>
                <Textarea
                  placeholder="Introduce yourself and explain why you're interested in this opportunity..."
                  value={enquiryForm.message}
                  onChange={(e) => setEnquiryForm({ ...enquiryForm, message: e.target.value })}
                  rows={5}
                  className="mt-1"
                />
              </div>

              {/* Summary */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">Summary of Your Interest</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-600">Interest Type:</span> <strong>{enquiryForm.interest_type}</strong></div>
                  <div><span className="text-gray-600">Budget:</span> <strong>{enquiryForm.investment_budget || 'Not specified'}</strong></div>
                  <div><span className="text-gray-600">Funding:</span> <strong>{enquiryForm.funding_source}</strong></div>
                  <div><span className="text-gray-600">Timeline:</span> <strong>{enquiryForm.target_timeline}</strong></div>
                  <div><span className="text-gray-600">Reason:</span> <strong>{enquiryForm.primary_reason}</strong></div>
                  <div><span className="text-gray-600">Meeting:</span> <strong>{enquiryForm.preferred_meeting_format}</strong></div>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-4 border-t mt-4">
            <Button
              variant="outline"
              onClick={() => {
                const tabs = ['investment', 'strategic', 'qualifications', 'process', 'message'];
                const currentIndex = tabs.indexOf(enquiryTab);
                if (currentIndex > 0) setEnquiryTab(tabs[currentIndex - 1]);
              }}
              disabled={enquiryTab === 'investment'}
            >
              Previous
            </Button>

            <div className="flex space-x-2">
              <Button variant="outline" onClick={() => setShowEnquiryDialog(false)}>
                Cancel
              </Button>

              {enquiryTab === 'message' ? (
                <Button onClick={handleEnquire} className="bg-blue-600 hover:bg-blue-700 text-white">
                  <Mail size={16} className="mr-2" />
                  Submit Interest
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    const tabs = ['investment', 'strategic', 'qualifications', 'process', 'message'];
                    const currentIndex = tabs.indexOf(enquiryTab);
                    if (currentIndex < tabs.length - 1) setEnquiryTab(tabs[currentIndex + 1]);
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Next
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MergersAcquisitions;
